import { Injectable } from '@angular/core';
import {
  IMapIssueIconProperty,
  IMapIssues,
} from '@app/core/interfaces/map.interface';
import {
  IMetroMapAdditional,
  IMetroMapConnection,
  IMetroMapData,
  IMetroMapStation,
  IMetroMapTransition,
} from '@app/core/interfaces/metro-map.interface';

@Injectable()
export class MetroMapService {
  public getMap(data: IMetroMapData, mapIssues: IMapIssues[] = []): string {
    let resultSvg: string = null;
    const width = data?.width ?? 4209;
    const height = data?.height ?? 3663;
    const startMap = `<svg width="100%" height="100%" viewBox="0 0 ${width} ${height}" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor">`;
    const endMap = `</svg>`;
    const stationLayer = this.getStationLayer(data?.stations ?? []);
    const connectionLayer = this.getConnectionLayer(data?.connections ?? []);
    const transitionLayer = this.getTransitionLayer(data?.transitions ?? []);
    const additionalLayer = this.getAdditionalLayer(data?.additional ?? []);
    const issueLayer = this.getIssueLayer(mapIssues);
    resultSvg =
      startMap +
      additionalLayer +
      connectionLayer +
      transitionLayer +
      stationLayer +
      issueLayer +
      endMap;
    return resultSvg;
  }

  private getStationLayer(stations: IMetroMapStation[]): string {
    const startStations = `<g id="Stations">`;
    const endStations = `</g>`;
    const stationsSvg: string[] =
      stations?.length > 0
        ? stations.map((station: IMetroMapStation) => {
            return station?.stationSvg?.svg ?? null;
          })
        : [];
    const resultStations = startStations + stationsSvg.join('') + endStations;
    const startCaptions = `<g id="Captions">`;
    const endCaptions = `</g>`;
    const captionsSvg: string[] =
      stations?.length > 0
        ? stations.map((station: IMetroMapStation) => {
            let svg = station?.textSvg?.svg ?? null;
            svg = svg?.replace('#1E1E1C', 'currentColor');
            svg = svg.replace('MoscowSans-Light', 'Moscow Sans');
            return svg;
          })
        : [];
    const resultCaptions = startCaptions + captionsSvg.join('') + endCaptions;
    return resultStations + resultCaptions;
  }

  private getConnectionLayer(connections: IMetroMapConnection[]): string {
    const startConnections = `<g id="Connections">`;
    const endConnections = `</g>`;
    const connectionsSvg: string[] =
      connections?.length > 0
        ? connections.map((connection: IMetroMapConnection) => {
            return connection?.svg ?? null;
          })
        : [];
    return startConnections + connectionsSvg.join('') + endConnections;
  }

  private getTransitionLayer(transitions: IMetroMapTransition[]): string {
    const startTransitions = `<g id="Connections">`;
    const endTransitions = `</g>`;
    const transitionsSvg: string[] =
      transitions?.length > 0
        ? transitions.map((transition: IMetroMapTransition) => {
            return transition?.svg ?? null;
          })
        : [];
    return startTransitions + transitionsSvg.join('') + endTransitions;
  }

  private getAdditionalLayer(additional: IMetroMapAdditional[]) {
    const additionalSvg: string[] =
      additional?.length > 0
        ? additional.map((add: IMetroMapAdditional) => {
            return add?.svg ?? null;
          })
        : [];
    return additionalSvg.join('');
  }

  public getStationPlace(
    stationId: number,
    data: IMetroMapData,
  ): {
    stationX: number;
    stationY: number;
  } {
    if (stationId) {
      if (data?.stations?.length > 0) {
        const station = data?.stations.find(
          (station: IMetroMapStation) => station.id === stationId,
        );
        return {
          stationX: station?.stationSvg?.x ?? null,
          stationY: station?.stationSvg?.y ?? null,
        };
      } else {
        return { stationX: null, stationY: null };
      }
    } else {
      return { stationX: null, stationY: null };
    }
  }

  private getIssueLayer(mapIssues: IMapIssues[]): string {
    let issueLayer = `<g id="issues">`;
    if (mapIssues?.length > 0) {
      mapIssues.forEach((issue) => {
        const issueStart = `<g id="issue_${issue.stationId}" cursor="pointer">`;
        const issueEnd = `</g>`;
        const iconProterty: IMapIssueIconProperty = this.getIconProperty(issue);
        const count =
          issue.countCriticalNnr +
          issue.countNnr +
          issue.countPpr +
          issue.countSec;
        const scale = 2.5;
        const iconX = issue.stationX
          ? Math.round(issue.stationX / scale) - iconProterty?.iconOffsetX
          : null;
        const iconY = issue.stationY
          ? Math.round(issue.stationY / scale) - iconProterty?.iconOffsetY
          : null;
        const countX = issue.stationX
          ? Math.round(issue.stationX) - iconProterty?.countOffsetX
          : null;
        const countY = issue.stationY
          ? Math.round(issue.stationY) + iconProterty?.countOffsetY
          : null;
        const iconElement = `<use 
        x="${iconX?.toString()}" 
        y="${iconY?.toString()}" 
        transform="scale(${scale})"
        xlink:href="/assets/images/mt-map-issue.svg#${iconProterty?.iconName}" />`;
        const countElement = `<text fill="${iconProterty?.countColor}" font-family="MoscowSans-Light" font-size="24">
        <tspan x="${countX?.toString()}" 
        y="${countY?.toString()}">
        ${count > 1 ? count.toString() : '!'}
        </tspan>
        </text>`;
        issueLayer =
          issueLayer + issueStart + iconElement + countElement + issueEnd;
      });
    }
    issueLayer = issueLayer + `</g>`;
    return issueLayer;
  }

  private getIconProperty(issue: IMapIssues): IMapIssueIconProperty {
    let result: IMapIssueIconProperty = null;
    if (issue) {
      if (issue.countCriticalNnr > 0) {
        if (issue.countNnr > 0 && issue.countSec > 0 && issue.countPpr > 0) {
          result = {
            iconName: 'crit_nnr_with_all',
            countColor: '#ffffff',
            iconOffsetX: 0,
            iconOffsetY: 0,
            countOffsetX: 5,
            countOffsetY: 8,
          };
        } else {
          if (issue.countNnr > 0) {
            result = {
              iconName: 'crit_nnr_with_nnr',
              countColor: '#ffffff',
              iconOffsetX: 0,
              iconOffsetY: 0,
              countOffsetX: 5,
              countOffsetY: 8,
            };
          } else {
            if (issue.countSec > 0 && issue.countPpr > 0) {
              result = {
                iconName: 'crit_nnr_with_sec_ppr',
                countColor: '#ffffff',
                iconOffsetX: 12,
                iconOffsetY: 12,
                countOffsetX: 5,
                countOffsetY: 8,
              };
            } else {
              if (issue.countSec > 0) {
                result = {
                  iconName: 'crit_nnr_with_sec',
                  countColor: '#ffffff',
                  iconOffsetX: 12,
                  iconOffsetY: 12,
                  countOffsetX: 5,
                  countOffsetY: 8,
                };
              } else if (issue.countPpr > 0) {
                result = {
                  iconName: 'crit_nnr_with_ppr',
                  countColor: '#ffffff',
                  iconOffsetX: 12,
                  iconOffsetY: 13,
                  countOffsetX: 5,
                  countOffsetY: 8,
                };
              } else {
                result = {
                  iconName: 'only_crit_nnr',
                  countColor: '#ffffff',
                  iconOffsetX: 11,
                  iconOffsetY: 11,
                  countOffsetX: 5,
                  countOffsetY: 8,
                };
              }
            }
          }
        }
      } else {
        if (issue.countNnr > 0) {
          if (issue.countSec > 0 && issue.countPpr > 0) {
            result = {
              iconName: 'nnr_with_sec_ppr',
              countColor: '#db2c0f',
              iconOffsetX: 12,
              iconOffsetY: 12,
              countOffsetX: 5,
              countOffsetY: 8,
            };
          } else {
            if (issue.countSec > 0) {
              result = {
                iconName: 'nnr_with_sec',
                countColor: '#db2c0f',
                iconOffsetX: 12,
                iconOffsetY: 12,
                countOffsetX: 5,
                countOffsetY: 8,
              };
            } else if (issue.countPpr > 0) {
              result = {
                iconName: 'nnr_with_ppr',
                countColor: '#db2c0f',
                iconOffsetX: 12,
                iconOffsetY: 13,
                countOffsetX: 5,
                countOffsetY: 8,
              };
            } else {
              result = {
                iconName: 'only_nnr',
                countColor: '#db2c0f',
                iconOffsetX: 12,
                iconOffsetY: 12,
                countOffsetX: 5,
                countOffsetY: 8,
              };
            }
          }
        } else {
          if (issue.countSec > 0) {
            if (issue.countPpr > 0) {
              result = {
                iconName: 'sec_with_ppr',
                countColor: '#ffffff',
                iconOffsetX: 12,
                iconOffsetY: 12,
                countOffsetX: 5,
                countOffsetY: 8,
              };
            } else {
              result = {
                iconName: 'only_sec',
                countColor: '#ffffff',
                iconOffsetX: 11,
                iconOffsetY: 9,
                countOffsetX: 5,
                countOffsetY: 8,
              };
            }
          } else {
            if (issue.countPpr > 0) {
              result = {
                iconName: 'only_ppr',
                countColor: '#db2c0f',
                iconOffsetX: 12,
                iconOffsetY: 12,
                countOffsetX: 4,
                countOffsetY: 7,
              };
            }
          }
        }
      }
    }
    return result;
  }
}
